/**
 * Encounter → equipment loot wiring (M7-B / M7-D2).
 * Generates elite/boss-kill drops and boss-chest contents exactly once per reward.
 * Uses dungeonLevel + mutation (never XP).
 */

import { ENEMY_TEMPLATES, getEnemyClassification } from '../data/enemies';
import {
  BOSS_CHEST_COUNT_BY_MUTATION,
  EQUIPMENT_DROP_CHANCE_BY_MUTATION,
  isDungeonMutationId,
  resolveDungeonProgression,
  type DungeonMutationId,
} from '../data/dungeonProgression';
import {
  generateBossChestLoot,
  generateLootItem,
  type GeneratedLootItem,
} from './loot';
import type { SeededRng } from './rng';

export function resolveDungeonMutation(
  value: string | undefined | null,
): DungeonMutationId {
  if (isDungeonMutationId(value ?? '')) return value as DungeonMutationId;
  return 'standard';
}

/** @deprecated Use dungeonLevel + mutation. */
export function resolveDungeonDifficulty(
  value: string | undefined | null,
): 'normal' | 'hard' | 'brutal' {
  if (value === 'hard' || value === 'brutal' || value === 'normal') {
    return value;
  }
  return 'normal';
}

export interface EncounterLootInput {
  encounterId: string;
  enemyTemplateIds: readonly string[];
  defeatedEnemies: ReadonlyArray<{ id: string; templateId: string }>;
  includeBossChest: boolean;
  dungeonLevel?: number;
  mutation?: DungeonMutationId;
  /** @deprecated Prefer dungeonLevel + mutation. */
  difficulty?: string;
}

export function generateEncounterEquipmentLoot(
  rng: SeededRng,
  input: EncounterLootInput,
): GeneratedLootItem[] {
  const migrated = resolveDungeonProgression({
    dungeonLevel: input.dungeonLevel,
    mutation: input.mutation,
    difficulty: input.difficulty,
  });
  const dungeonLevel = migrated.dungeonLevel;
  const mutation = migrated.mutation;
  const dropChances = EQUIPMENT_DROP_CHANCE_BY_MUTATION[mutation];
  const items: GeneratedLootItem[] = [];

  for (const enemy of input.defeatedEnemies) {
    const template = ENEMY_TEMPLATES[enemy.templateId];
    if (!template) continue;
    const classification = getEnemyClassification(template);
    if (classification === 'ordinary') {
      continue;
    }
    if (classification === 'elite') {
      if (rng.next() < dropChances.elite) {
        items.push(
          generateLootItem(rng, {
            dungeonLevel,
            mutation,
            source: 'elite_drop',
            idSalt: `${input.encounterId}_${enemy.id}`,
          }),
        );
      }
    } else if (classification === 'boss') {
      if (rng.next() < dropChances.bossKill) {
        items.push(
          generateLootItem(rng, {
            dungeonLevel,
            mutation,
            source: 'boss_kill',
            idSalt: `${input.encounterId}_kill_${enemy.id}`,
          }),
        );
      }
    }
  }

  if (input.includeBossChest) {
    items.push(
      ...generateBossChestLoot(rng, dungeonLevel, mutation, input.encounterId),
    );
  }

  return items;
}

export { BOSS_CHEST_COUNT_BY_MUTATION };
